import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CpuIcon, LandmarkIcon, MapPinIcon, TrendingUpIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';

/** One visual per case, matched to the function being hired: finance, engineering, commercial. */
const caseVisuals = [{
  icon: <LandmarkIcon className="w-5 h-5" data-id="element-524" data-mp="u9xe08uit6a3" />,
  tone: 'from-primary-100 to-primary-50 text-primary-600'
}, {
  icon: <CpuIcon className="w-5 h-5" data-id="element-525" data-mp="q8vd7nkygm60" />,
  tone: 'from-violet-100 to-violet-50 text-violet-600'
}, {
  icon: <TrendingUpIcon className="w-5 h-5" data-id="element-526" data-mp="l7frdtayebmk" />,
  tone: 'from-emerald-100 to-emerald-50 text-emerald-600'
}];

/**
 * PLACEHOLDER CONTENT — the three cases come from i18n/escaCopy.ts and are
 * illustrative constructions. Replace with verified stories before launch.
 */
export function ClassicCases() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="cases" className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-527" data-mp="6f8mxn073ag8">
      <div className="absolute inset-0 pointer-events-none" data-id="element-528" data-mp="3oktsyhnrqouw">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-cyan-50/30 blur-3xl hidden sm:block" data-id="element-529" data-mp="3tkpv0bcwo67t" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-primary-50/40 blur-3xl hidden sm:block" data-id="element-530" data-mp="3evtnqgioax89" />
        <div className="absolute inset-0 opacity-[0.018]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(15,23,42,1) 1px, transparent 0)`,
        backgroundSize: '48px 48px'
      }} data-id="element-531" data-mp="3jod39j0x82fp" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-532" data-mp="2vwxvvbuaeuxl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-12 lg:mb-14" data-id="element-533" data-mp="30wka9oluqh7z">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-534" data-mp="2d078xa17logr">
            Cases
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight max-w-4xl mx-auto" data-id="element-535" data-mp="27zrwqlsnyn0d">
            {c.cases.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto leading-relaxed" data-id="element-536" data-mp="30p8m1akzlnsd">
            {c.cases.lead}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6" data-id="element-537" data-mp="2qsdksjxbx9ae">
          {c.cases.items.map((item, i) => {
          return <motion.article key={item.role + item.industry} initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1 + i * 0.08
          }} className="flex flex-col rounded-2xl bg-white border border-navy-200 shadow-sm overflow-hidden" data-id="element-538" data-mp="2lu2c4cirt4tr" data-mpi={i}>
                <div className="p-6 lg:p-7 flex-1 flex flex-col" data-id="element-539" data-mp="3r0ajqkxmajit">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${caseVisuals[i % caseVisuals.length].tone}`} data-id="element-540" data-mp="3qyhftdee2dei">
                    {caseVisuals[i % caseVisuals.length].icon}
                  </div>

                  <h3 className="mt-5 text-2xl font-extrabold text-navy-900 tracking-tight" data-id="element-541" data-mp="dl4jvvbmij3x">
                    {item.role}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-542" data-mp="iljvahcchgj9">
                    <span className="px-2.5 py-1 rounded-md bg-navy-50 text-xs font-medium text-navy-600" data-id="element-543" data-mp="nk3kcqm8j8fu">
                      {item.industry}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-navy-50 text-xs font-medium text-navy-600" data-id="element-544" data-mp="2pb6wjkbrwv2i">
                      <MapPinIcon className="w-3 h-3" data-id="element-545" data-mp="jlv4m8h7l47e" />
                      {item.geo}
                    </span>
                  </div>

                  <div className="mt-6 pt-6 border-t border-navy-100" data-id="element-546" data-mp="epxljuek9jqx">
                    <p className="text-xs font-semibold uppercase tracking-wide text-navy-400" data-id="element-547" data-mp="sn2o3kf2g0hf">
                      {c.cases.challengeLabel}
                    </p>
                    <p className="mt-2 text-sm text-navy-600 leading-relaxed" data-id="element-548" data-mp="vwsnhaaiqyh">
                      {item.challenge}
                    </p>
                  </div>

                  <div className="pt-5" data-id="element-549" data-mp="3s0wnz82ao9w2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-navy-400" data-id="element-550" data-mp="9p08fw8erfwa">
                      {c.cases.approachLabel}
                    </p>
                    <p className="mt-2 text-sm text-navy-600 leading-relaxed" data-id="element-551" data-mp="3ddto4uqi1p5k">
                      {item.approach}
                    </p>
                  </div>

                  <div className="mt-auto pt-6" data-id="element-552" data-mp="39bzrcxjfd5ao">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-600" data-id="element-553" data-mp="2z7rri4xd79ma">
                      {c.cases.resultLabel}
                    </p>
                    <p className="mt-2 text-base font-bold text-navy-900 leading-snug" data-id="element-554" data-mp="340un0ilpct54">
                      {item.result}
                    </p>
                  </div>
                </div>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}